import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AppLink from './AppLink';
import { useAuth } from '../context/AuthContext';
import { LogOut, User as UserIcon, Menu, X } from 'lucide-react';
import { getPostAuthPath } from '../utils/authRouting';
import BrandLogo from './BrandLogo';

const Navigation = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const dashboardPath = user ? getPostAuthPath(user) : '/login';
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { to: '/for-brands', label: 'For Brands' },
    { to: '/for-creators', label: 'For Creators' },
    { to: '/how-it-works', label: 'How It Works' },
    { to: '/case-studies', label: 'Case Studies' },
    { to: '/about', label: 'About' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <AppLink to="/" className="flex items-center gap-2 group">
              <BrandLogo sizeClass="h-8" />
            </AppLink>
          </div>

          {/* Desktop Navigation Menu */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map(({ to, label }) => (
              <AppLink key={to} to={to} className="text-dark-400 hover:text-white transition-colors duration-200 text-[13px] font-medium px-3.5 py-2 rounded-lg hover:bg-white/[0.04]">
                {label}
              </AppLink>
            ))}
          </div>

          {/* Desktop CTA / User Profile */}
          <div className="hidden md:flex items-center">
            {isAuthenticated && user ? (
              <div className="flex items-center gap-2.5">
                <AppLink 
                  to={dashboardPath}
                  className="text-primary hover:text-primary-300 transition-colors duration-200 text-[13px] font-semibold px-3.5 py-2 rounded-lg hover:bg-primary/[0.06]"
                >
                  Dashboard
                </AppLink>
                <div className="flex items-center gap-2">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full ring-1 ring-white/10" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center">
                      <UserIcon className="w-3.5 h-3.5 text-primary" />
                    </div>
                  )}
                  <span className="text-white font-medium text-[13px]">{user.name}</span>
                </div>
                <button
                  onClick={logout}
                  className="bg-white/[0.05] hover:bg-white/[0.08] text-dark-300 hover:text-white px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-200 flex items-center gap-1.5 border border-white/[0.06]"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <AppLink to="/join" className="bg-primary text-dark-950 px-5 py-2 rounded-xl text-[13px] font-semibold hover:bg-primary-300 hover:shadow-glow transition-all duration-200 active:scale-[0.97]">
                Get Started
              </AppLink>
            )}
          </div>

          {/* Mobile hamburger button */}
          <button
            className="md:hidden p-2 text-white hover:text-primary transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile slide-out drawer */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-dark-950/60 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <div className="fixed top-0 right-0 h-full w-72 bg-dark-950 border-l border-surface-border z-50 md:hidden flex flex-col animate-slide-in-right">
            {/* Close button */}
            <div className="flex justify-end p-4">
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-dark-400 hover:text-white transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Nav links */}
            <div className="flex flex-col px-6 space-y-1">
              {navLinks.map(({ to, label }) => (
                <AppLink
                  key={to}
                  to={to}
                  className="text-dark-300 hover:text-white hover:bg-surface-light px-4 py-3 rounded-xl transition-colors text-sm font-medium"
                >
                  {label}
                </AppLink>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-surface-border mx-6 my-4" />

            {/* Auth section */}
            <div className="px-6 flex flex-col space-y-3">
              {isAuthenticated && user ? (
                <>
                  <div className="flex items-center gap-3 px-4 py-2">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-surface-border" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <UserIcon className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <span className="text-white font-medium text-sm truncate">{user.name}</span>
                  </div>
                  <AppLink
                    to={dashboardPath}
                    className="text-primary hover:text-primary-300 px-4 py-3 rounded-xl hover:bg-surface-light transition-colors font-semibold text-sm"
                  >
                    Dashboard
                  </AppLink>
                  <button
                    onClick={logout}
                    className="flex items-center gap-2 text-red-400 hover:text-red-300 px-4 py-3 rounded-xl hover:bg-surface-light transition-colors font-medium text-sm text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <AppLink
                    to="/login"
                    className="text-dark-300 hover:text-white px-4 py-3 rounded-xl hover:bg-surface-light transition-colors text-sm font-medium"
                  >
                    Login
                  </AppLink>
                  <AppLink
                    to="/join"
                    className="bg-primary text-dark-950 px-4 py-3 rounded-xl font-semibold hover:bg-primary-300 transition-colors text-center text-sm"
                  >
                    Join
                  </AppLink>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navigation;
